<template>
  <v-tooltip right :disabled="!showTooltip">
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        x-small
        v-bind="attrs"
        v-on="on"
        class="collapse-toggle-btn"
        :class="buttonClasses"
        :style="buttonStyle"
        @click="toggleCollapse"
        :aria-label="ariaLabel"
        :aria-expanded="!arePanelsCollapsed"
      >
        <v-icon size="16">{{ iconName }}</v-icon>
      </v-btn>
    </template>
    <span>{{ tooltipText }}</span>
  </v-tooltip>
</template>

<script>
import { mapGetters } from 'vuex';

/**
 * CollapseToggle Component
 *
 * A small floating button that toggles the collapse/expand state of panels.
 * Positioned at the right edge of the navigation area.
 *
 * @props {Boolean} showTooltip - Whether to show tooltip on hover
 *
 * Behavior:
 * - When panels expanded: Arrow points left (←) = "click to collapse"
 * - When panels collapsed: Arrow points right (→) = "click to expand"
 */
export default {
  name: 'CollapseToggle',

  props: {
    /**
     * Whether to show tooltip on hover
     */
    showTooltip: {
      type: Boolean,
      default: true,
    },

    /**
     * Whether any panel is currently being resized
     * Disables position transition during resize for smoother UX
     */
    isResizing: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters('GENERAL', {
      hasPanels: 'GET_HAS_PANELS',
      arePanelsCollapsed: 'GET_ARE_PANELS_COLLAPSED',
      totalNavWidth: 'GET_TOTAL_NAV_WIDTH',
      railWidth: 'GET_RAIL_WIDTH',
      panels: 'GET_PANELS',
    }),

    /**
     * Button position style
     * Attaches to the right edge of the last panel (or rail if collapsed)
     */
    buttonStyle() {
      // When collapsed, position at rail edge
      // When expanded, position at the right edge of all panels
      const leftPosition = this.arePanelsCollapsed
        ? `var(--ds-nav-rail-collapsed)` // Attach to rail edge
        : `${this.totalNavWidth}px`; // Attach to last panel edge

      return {
        left: leftPosition,
      };
    },

    /**
     * Button CSS classes
     */
    buttonClasses() {
      return {
        'is-collapsed': this.arePanelsCollapsed,
        'is-expanded': !this.arePanelsCollapsed,
        'is-resizing': this.isResizing,
      };
    },

    /**
     * Icon based on current state
     */
    iconName() {
      return this.arePanelsCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-left';
    },

    /**
     * Tooltip text
     */
    tooltipText() {
      return this.arePanelsCollapsed ? 'Expand panels' : 'Collapse panels';
    },

    /**
     * Accessibility label
     */
    ariaLabel() {
      return this.arePanelsCollapsed
        ? 'Expand navigation panels'
        : 'Collapse navigation panels';
    },
  },

  methods: {
    /**
     * Toggle panels collapsed state
     */
    toggleCollapse() {
      this.$store.dispatch('GENERAL/TOGGLE_PANELS_COLLAPSED');
      this.$emit('toggle', !this.arePanelsCollapsed);
    },
  },
};
</script>

<style lang="scss" scoped>
.collapse-toggle-btn {
  pointer-events: all;
  position: fixed;
  bottom: 50px;
  transform: translate(-50%, -50%);
  z-index: 110; // Above panels
  width: 24px !important;
  height: 24px !important;
  min-width: 24px !important;
  background: var(--v-bg2-base) !important;
  border: 1px solid var(--ds-border-subtle);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  transition:
    left var(--ds-transition-rail),
    background-color 150ms ease,
    transform 150ms ease;

  // Disable left transition during resize for smooth tracking
  &.is-resizing {
    display: none;
    transition:
      background-color 150ms ease,
      transform 150ms ease;
  }

  &:hover {
    background: #0a2626 !important;

    .v-icon {
      color: white;
    }
  }

  &:focus {
    outline: 1px solid var(--ds-border-subtle);
    outline-offset: 1px;
  }

  .v-icon {
    color: var(--v-grey-base);
    transition: color 150ms ease, transform 200ms ease;
  }

  // Animate icon when state changes
  &.is-collapsed {
    .v-icon {
      animation: nudge-right 0.3s ease;
    }
  }

  &.is-expanded {
    .v-icon {
      animation: nudge-left 0.3s ease;
    }
  }
}

// Subtle animation to draw attention to the toggle
@keyframes nudge-right {
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(3px); }
}

@keyframes nudge-left {
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(-3px); }
}

// Mobile: hide the toggle (panels are full-screen)
@media (max-width: 768px) {
  .collapse-toggle-btn {
    display: none;
  }
}
</style>
